import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setPartnerProfile,
  setPartnerProfileLoading,
  setPartnerProfileError,
  clearPartnerProfile,
  clearUser,
} from "./redux/authSlice";
import axiosInstance, { setAccessToken } from "./utils/axiosInstance";
import { isAmxAdmin, isAmxUser } from "./utils/auth";

// Pages 
import Dashboard from './pages/Dashboard';
import RequireAuth from './components/RequireAuth';
import Login from './components/Login';
import About from './pages/About';
import AmxGatewayManagement from './pages/AmxGatewayManagement';
import CapabilitiesExplorer from './pages/BrowseCapabilities';
import CapabilityCreatePage from './pages/CapabilityCreatePage';
import CapabilityDetailPage from './pages/CapabilityDetailPage';
import CapabilityRequestPage from './pages/CapabilityRequestPage';
import CapabilitiesDatabase from './pages/CapabilitiesDatabase';
import BusinessUnitDetailPage from './pages/BusinessUnitDetailPage';
import BusinessUnitsPage from './pages/BusinessUnitsPage';
import UsersPage from './pages/UsersPage';
import AssetManagementPage from './pages/AssetManagementPage';
import AssetsPage from './pages/AssetsPage';
import AssetCreatePage from './pages/AssetCreatePage';
import AssetRealtimeDashboard from './pages/AssetRealtimeDashboard';
import AssetRunDetailPage from './pages/AssetRunDetailPage';
import TDP from './pages/TDP';
import CreateTDP from './pages/CreateTDP';
import TDPDetailPage from './pages/TDPDetailPage';
import WorkspacesDashboard from './pages/WorkspacesDashboard';
import MyServices from './pages/MyServices';
import WorkspaceView from './pages/WorkspaceView';
import LandingPage from './pages/LandingPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import PartnerOnboardingPage from './pages/PartnerOnboardingPage';
import PreferredPartnerLists from './pages/PreferredPartnerLists';
import MyAccount from './pages/MyAccount';
import PartnerAdminDashboard from './pages/PartnerAdminDashboard';
import DirectRequestDetailPage from './pages/DirectRequestDetailPage';
import DiscoveryRequestPage from './pages/DiscoveryRequestPage';
import NeedOpportunityDetailPage from './pages/NeedOpportunityDetailPage';
import RequestCreatePage from './pages/RequestCreatePage';
import RequestDetailPage from './pages/RequestDetailPage';
import RequestsStubPage from './pages/RequestsStubPage';
import MyWorkPage from './pages/MyWorkPage';
import RequireGroup from './components/RequireGroup';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPartnersPage from './pages/admin/AdminPartnersPage';
import AdminPartnerDetailPage from './pages/admin/AdminPartnerDetailPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminMqttTopicsPage from './pages/admin/AdminMqttTopicsPage';
import AdminGatewaysPage from './pages/admin/AdminGatewaysPage';
import AdminNeedCategoriesPage from './pages/admin/AdminNeedCategoriesPage';
import AdminNeedCategoryDetailPage from './pages/admin/AdminNeedCategoryDetailPage';
import AdminAssetTypesPage from './pages/admin/AdminAssetTypesPage';
import AdminSharedSchemasPage from './pages/admin/AdminSharedSchemasPage';
import AdminWorkspacesPage from './pages/admin/AdminWorkspacesPage';
import AdminDemoWorkspacePage from './pages/admin/AdminDemoWorkspacePage';
import AdminAccountSecurityPage from './pages/admin/AdminAccountSecurityPage';
import AdminAccountCompliancePage from './pages/admin/AdminAccountCompliancePage';
import AdminActivityLogsPage from './pages/admin/AdminActivityLogsPage';
import Feedback from './pages/Feedback';

function RootRoute() {
  const location = useLocation();
  const auth = useAuth();
  const storedUser = useSelector((state) => state.auth.user);
  const token = new URLSearchParams(location.search).get('token');
  const activeUser = storedUser || auth.user?.profile;

  if (token) {
    return <Navigate to={`/invites/accept?token=${encodeURIComponent(token)}`} replace />;
  }

  if (isAmxAdmin(activeUser) && !isAmxUser(activeUser)) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <RequireAuth>
      <Dashboard />
    </RequireAuth>
  );
}

export default function App() {
  const auth = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      dispatch(setUser({
        user: auth.user.profile,
        id_token: auth.user.id_token,
        access_token: auth.user.access_token,
        refresh_token: auth.user.refresh_token,
      }));

      if (auth.user.access_token) {
        setAccessToken(auth.user.access_token);
      }
    } else {
      // 🔥 Clear Redux when auth is no longer valid
      dispatch(clearUser());
      setAccessToken(null);
    }
  }, [auth.isAuthenticated, auth.user, dispatch]);

  useEffect(() => {
    let isActive = true;

    async function fetchPartnerProfile() {
      if (!auth.isAuthenticated || !auth.user?.access_token) {
        dispatch(clearPartnerProfile());
        return;
      }

      try {
        setAccessToken(auth.user.access_token);
        dispatch(setPartnerProfileLoading());
        const response = await axiosInstance.get('/account/me');

        if (!isActive) {
          return;
        }

        dispatch(setPartnerProfile(response.data));
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error('Failed to fetch authenticated user profile', {
          message: error?.message,
          status: error?.response?.status,
          data: error?.response?.data,
        });
        dispatch(setPartnerProfileError());
      }
    }

    fetchPartnerProfile();

    return () => {
      isActive = false;
    };
  }, [auth.isAuthenticated, auth.user?.access_token, dispatch]);

  return (
    <Router>
      <Routes>
        {/* Public Route */}
        <Route path='/welcome' element={<LandingPage />} />
        <Route path='/login' element={<Login />} />
        <Route path='/invites/accept' element={<InviteAcceptPage />} />

        {/* Protected Routes */}
        <Route path='/' element={<RootRoute />} />

        <Route path='/amx-gateway-management' element={
          <RequireAuth>
            <AmxGatewayManagement />
          </RequireAuth>
        } />

        <Route path='/capabilities-database' element={
          <RequireAuth>
            <Navigate to="/my-capabilities" replace />
          </RequireAuth>
        } />

        <Route path='/my-capabilities' element={
          <RequireAuth>
            <CapabilitiesDatabase />
          </RequireAuth>
        } />

        <Route path='/capabilities/new' element={
          <RequireAuth>
            <CapabilityCreatePage />
          </RequireAuth>
        } />

        <Route path='/capabilities/:capabilityDefinitionId/request' element={
          <RequireAuth>
            <CapabilityRequestPage />
          </RequireAuth>
        } />

        <Route path='/capabilities/:capabilityDefinitionId' element={
          <RequireAuth>
            <CapabilityDetailPage />
          </RequireAuth>
        } />

        <Route path='/business-units' element={
          <RequireAuth>
            <BusinessUnitsPage />
          </RequireAuth>
        } />

        <Route path='/users' element={<RequireAuth><UsersPage /></RequireAuth>} />

        <Route path='/business-units/:businessUnitId' element={
          <RequireAuth>
            <BusinessUnitDetailPage />
          </RequireAuth>
        } />

        <Route path='/business-units/:businessUnitId/capabilities/new' element={
          <RequireAuth>
            <CapabilityCreatePage />
          </RequireAuth>
        } />

        <Route path='/business-units/:businessUnitId/assets/new' element={
          <RequireAuth>
            <AssetCreatePage />
          </RequireAuth>
        } />

        <Route path='/assets/new' element={
          <RequireAuth>
            <AssetCreatePage />
          </RequireAuth>
        } />

        <Route path='/assets' element={
          <RequireAuth>
            <AssetsPage />
          </RequireAuth>
        } />

        <Route path='/assets/:assetId' element={
          <RequireAuth>
            <AssetManagementPage />
          </RequireAuth>
        } />

        <Route path='/assets/:assetId/realtime' element={
          <RequireAuth>
            <AssetRealtimeDashboard />
          </RequireAuth>
        } />

        <Route path='/runs/:runId' element={
          <RequireAuth>
            <AssetRunDetailPage />
          </RequireAuth>
        } />

        <Route path='/browse-capabilities' element={
          <RequireAuth>
            <CapabilitiesExplorer />
          </RequireAuth>
        } />

        <Route path='/capabilities-explorer' element={
          <RequireAuth>
            <CapabilitiesExplorer />
          </RequireAuth>
        } />

        <Route path='/x-tdp' element={
          <RequireAuth>
            <TDP />
          </RequireAuth>
        } />

        <Route path='/create-tdp' element={
          <RequireAuth>
            <CreateTDP />
          </RequireAuth>
        } />

        <Route path='/tdp/:tdp_id' element={
          <RequireAuth>
            <TDPDetailPage />
          </RequireAuth>
        } />


        <Route path='/workspaces-dashboard' element={
          <RequireAuth>
            <WorkspacesDashboard />
          </RequireAuth>
        } />

        <Route path='/requests' element={
          <RequireAuth>
            <RequestsStubPage />
          </RequireAuth>
        } />

        <Route path='/my-work' element={
          <RequireAuth>
            <MyWorkPage />
          </RequireAuth>
        } />

        <Route path='/requests/discover' element={
          <RequireAuth>
            <DiscoveryRequestPage />
          </RequireAuth>
        } />

        <Route path='/requests/new' element={
          <RequireAuth>
            <RequestCreatePage />
          </RequireAuth>
        } />

        <Route path='/requests/need-opportunities/:requestId' element={
          <RequireAuth>
            <NeedOpportunityDetailPage />
          </RequireAuth>
        } />

        <Route path='/requests/direct/:requestCapabilityId' element={
          <RequireAuth>
            <DirectRequestDetailPage />
          </RequireAuth>
        } />

        <Route path='/requests/:requestId' element={
          <RequireAuth>
            <RequestDetailPage />
          </RequireAuth>
        } />

        <Route path='/workspaces/:workspace_id' element={
          <RequireAuth>
            <WorkspaceView />
          </RequireAuth>
        } />

        <Route path='/about' element={
          <RequireAuth>
            <About />
          </RequireAuth>
        } />

        <Route path='/my-services' element={
          <RequireAuth>
            <MyServices />
          </RequireAuth>
        } />

        <Route path='/preferred-partner-lists' element={
          <RequireAuth>
            <PreferredPartnerLists />
          </RequireAuth>
        } />

        <Route path='/my-account' element={
          <RequireAuth>
            <MyAccount />
          </RequireAuth>
        } />

        <Route path='/partner-onboarding' element={
          <RequireAuth>
            <PartnerOnboardingPage />
          </RequireAuth>
        } />

        <Route path='/partner-admin' element={
          <RequireAuth>
            <RequireGroup group='partner-admin'>
              <PartnerAdminDashboard />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminDashboard />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/partners' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminPartnersPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/partners/:partner_id' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminPartnerDetailPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/users' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminUsersPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/mqtt-topics' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminMqttTopicsPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/gateways' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminGatewaysPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/need-categories' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminNeedCategoriesPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/need-categories/:categoryId' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminNeedCategoryDetailPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/asset-types' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminAssetTypesPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/shared-schemas' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminSharedSchemasPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/account-security' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminAccountSecurityPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/account-compliance' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminAccountCompliancePage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/activity-logs' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminActivityLogsPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/workspaces' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminWorkspacesPage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/admin/demo-workspace' element={
          <RequireAuth>
            <RequireGroup group='amx-admin'>
              <AdminDemoWorkspacePage />
            </RequireGroup>
          </RequireAuth>
        } />

        <Route path='/feedback' element={
          <RequireAuth>
            <Feedback />
          </RequireAuth>
        } />


      </Routes>
    </Router>
  );
}
